/**
 * Shapes the FDR scorecard feed (vendors, metrics with benchmarks, monthly
 * scores) into the Charting board's measure list: vendor → domain (sheet) →
 * KPI. Pure and deterministic; the same numbers the Scorecards screen shows.
 *
 *   percent KPIs     the value as a rate, benchmark as stored
 *   yes/no KPIs      100 for yes, 0 for no, benchmark 100 (met = yes)
 *   number KPIs      the value as a count, benchmark as stored
 *   benchmark_op     '<=' or '<' reads as lower is better; anything else higher
 *   met              the stored verdict rides the month (met / missed)
 */
const slug = (s) => String(s || 'domain').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'domain';

export function buildScorecardCharting({ vendors = [], metrics = [], scores = [] }) {
  const vendorName = new Map(vendors.map((v) => [String(v.id), v.name]));
  const areaMap = new Map();
  const byMetric = new Map();
  for (const m of metrics) {
    const areaKey = slug(m.sheet_name);
    if (!areaMap.has(areaKey)) areaMap.set(areaKey, { key: areaKey, label: m.sheet_name || 'Domain', full: `${m.sheet_name || 'Domain'} (scorecard domain)` });
    const lower = ['<=', '<'].includes(String(m.benchmark_op || '').trim());
    const unit = m.kind === 'percent' || m.kind === 'yesno' ? 'percent' : 'count';
    const benchmark = m.kind === 'yesno' ? 100 : (m.benchmark_value !== null && m.benchmark_value !== undefined ? Number(m.benchmark_value) : null);
    byMetric.set(String(m.id), {
      id: String(m.id),
      label: m.category ? `${m.category} · ${m.kpi_text}` : m.kpi_text,
      entity: vendorName.get(String(m.fdr_entity_id)) || 'Vendor',
      area: areaKey,
      benchmark,
      direction: lower ? 'lower' : 'higher',
      unit,
      kind: m.kind,
      periods: new Map(),
      cases: new Map(),
    });
  }
  for (const s of scores) {
    const a = byMetric.get(String(s.metric_id));
    if (!a) continue;
    const ym = `${s.year}-${String(s.month).padStart(2, '0')}`;
    let value = s.value_num === null || s.value_num === undefined ? null : Number(s.value_num);
    if (a.kind === 'yesno' && value !== null) value = value ? 100 : 0;
    if (value === null && (s.met === null || s.met === undefined)) continue;
    a.periods.set(ym, {
      num: 0, den: 0, defs: 0, value,
      status: s.met === true ? 'met' : s.met === false ? 'missed' : null,
    });
  }
  const measures = [...byMetric.values()].filter((a) => a.periods.size > 0).sort((x, y) => x.entity.localeCompare(y.entity) || x.label.localeCompare(y.label));
  const areas = [...areaMap.values()].filter((ar) => measures.some((a) => a.area === ar.key));
  return { measures, areas };
}

export default { buildScorecardCharting };
